import { Paper, Stack, Text } from '@mantine/core';
import { Debit } from 'interfaces';
import { formatDateOnly } from 'utils/dateUtils';

interface PaidTariffPaymentSummaryProps {
  tariffs: Array<{
    id: string;
    prefix?: string | number | null;
    code?: string | number | null;
  }>;
  debits: Debit[];
  tariffIDs?: string[];
  timezone?: string | null;
  showWarning?: boolean;
}

export const hasPaidTariffDebits = (
  debits: Debit[] = [],
  tariffIDs?: string[]
): boolean => {
  const tariffIDSet = tariffIDs ? new Set(tariffIDs) : null;

  return debits.some(
    (debit) =>
      Number(debit.paidAmount ?? 0) > 0 &&
      (!tariffIDSet || tariffIDSet.has(debit.tariffID))
  );
};

const formatCurrency = (value: string | number | null | undefined) =>
  Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
  }).format(Number(value ?? 0) || 0);

export const PaidTariffPaymentSummary = ({
  tariffs,
  debits,
  tariffIDs,
  timezone,
  showWarning = true,
}: PaidTariffPaymentSummaryProps) => {
  const tariffIDSet = new Set(tariffIDs ?? tariffs.map((tariff) => tariff.id));
  const paidDebits = debits.filter(
    (debit) =>
      tariffIDSet.has(debit.tariffID) && Number(debit.paidAmount ?? 0) > 0
  );

  if (paidDebits.length === 0) return null;

  const tariffByID = new Map(tariffs.map((tariff) => [tariff.id, tariff]));
  const paidDebitsByTariffID = paidDebits.reduce<Map<string, Debit[]>>(
    (grouped, debit) => {
      grouped.set(debit.tariffID, [
        ...(grouped.get(debit.tariffID) ?? []),
        debit,
      ]);
      return grouped;
    },
    new Map()
  );

  return (
    <Stack gap="sm" w="100%">
      {showWarning ? (
        <Text fw={600} ta="center">
          Are you sure you want to resubmit these tariffs with payments?
        </Text>
      ) : null}
      {[...paidDebitsByTariffID.entries()].map(([tariffID, tariffDebits]) => {
        const tariff = tariffByID.get(tariffID);
        const tariffLabel =
          `${tariff?.prefix ?? ''}${tariff?.code ?? ''}`.trim() || 'Tariff';

        return (
          <Paper key={tariffID} withBorder radius="md" p="sm">
            <Stack gap={4}>
              <Text fw={600}>{tariffLabel}</Text>
              {tariffDebits.map((debit) => (
                <Text key={debit.id} size="sm" c="dimmed">
                  {formatCurrency(debit.paidAmount)} paid on{' '}
                  {formatDateOnly(debit.createdAt, {
                    timezone,
                    fallback: 'Unknown date',
                  })}
                </Text>
              ))}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};
